import { useMutation } from '@tanstack/react-query';
import { Effect } from 'effect';
import {
  deleteNoteEffect,
  listNotesEffect,
  type NoteRow,
  type NoteUpsertInput,
  notesQueryKey,
  upsertNotesEffect,
} from '../notes';
import { useAppRuntime } from '../runtime-context';
import { createListEntity } from './optimistic-list-entity';

// Per-entity collection-shaped hooks over the `notesQueryKey` list cache.
// Shared across apps (start / browser-ext / mobile) — runtime resolved via
// `AppRuntimeProvider`. See docs/projects/complete/web/shared-tanstack-query.md.
//
// Optimistic update (edit title/body of an existing note) and delete. Inserts
// (new notes) still go through `useUpsertNoteMutation` below, because the server
// assigns the id — there's no local row to flip optimistically. The update op
// reuses the shared server upsert; the onSettled refetch reconciles any
// server-side field transforms.
// Background poll (matches pre-migration) so notes created on another device
// surface in the read-along sidebar without a manual refresh. Paused while the
// tab is backgrounded (TanStack default).
const NOTES_POLL_INTERVAL_MS = 5 * 60 * 1000;

const notesEntity = createListEntity({
  queryKey: notesQueryKey,
  listEffect: () => listNotesEffect(),
  getId: (n: NoteRow) => n.id,
  refetchInterval: NOTES_POLL_INTERVAL_MS,
  operations: {
    // Edit of an existing note: persist the changed row via the shared upsert,
    // carrying through the anchor metadata so an edit never drops it.
    update: ({ next }) =>
      upsertNotesEffect([
        {
          id: next.id,
          title: next.title,
          body: next.body,
          audio_conversion_id: next.audio_conversion_id,
          chapter_id: next.chapter_id,
          chapter_audio_offset_ms: next.chapter_audio_offset_ms,
          chapter_word_offset: next.chapter_word_offset,
          tts_id: next.tts_id,
          doc_pack_page: next.doc_pack_page,
          doc_pack_char_start: next.doc_pack_char_start,
          doc_pack_char_end: next.doc_pack_char_end,
        },
      ]),
    remove: ({ id }) => deleteNoteEffect(id),
  },
});

export const useNotesCollection = notesEntity.useEntity;
// Notes list is read in the read-along sidebar off various routes → non-suspense.
export const useNotesListQuery = notesEntity.useListQuery;

// Filtered notes read with a legacy-infinite-query surface, shared across apps.
// The list query drains every cursor, so the data is always complete and
// pagination collapses (`hasNextPage` always false). `limit` is accepted for
// legacy-callsite compatibility but ignored.
export const useFilteredNotesQuery = (opts: {
  audio_conversion_id?: string;
  limit?: number;
  /** Pass `false` to skip the fetch entirely — e.g. readers without a note
   * target, where the request would be useless (or unauthorized for public
   * visitors). */
  enabled?: boolean;
}) => {
  const query = useNotesListQuery(
    (rows) =>
      opts.audio_conversion_id
        ? rows.filter((n) => n.audio_conversion_id === opts.audio_conversion_id)
        : rows,
    { enabled: opts.enabled ?? true },
  );
  return {
    data: query.data ?? [],
    isPending: query.isPending,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? undefined,
    refetch: query.refetch,
    hasNextPage: false,
    fetchNextPage: () => Promise.resolve(),
    isFetchingNextPage: false,
  };
};

// Upsert notes + refetch the collection. The server assigns ids for new notes
// and may transform fields, so there's no optimistic insert/update — call
// upsert, then refetch. Runs on the app runtime via `AppRuntimeProvider`.
export const useUpsertNoteMutation = () => {
  const collection = useNotesCollection();
  const runtime = useAppRuntime();
  return useMutation({
    mutationFn: (notes: ReadonlyArray<NoteUpsertInput>) =>
      runtime.runPromise(
        Effect.gen(function* () {
          const upserted = yield* upsertNotesEffect(notes);
          yield* Effect.promise(() => collection.utils.refetch());
          yield* Effect.logInfo('Notes updated successfully');
          return upserted;
        }),
      ),
  });
};
